export type StatusColorClass = string;

const STATUS_COLORS = {
  green: [
    "available",
    "online",
    "completed",
    "success",
    "finished",
    "verified",
    "captured",
  ],
  blue: [
    "active",
    "all_active",
    "starting",
    "starting_from_charge_station",
    "authenticating",
    "in_use",
    "in use",
    "rented",
    "reserved",
    "in_station",
    "in station",
    "charging",
    "virtual",
    "refunded",
    "reversed",
  ],
  yellow: [
    "pending",
    "warning",
    "faulty",
    "pause",
    "waiting_stop_point",
    "authorized",
    "unpaid",
    "wallet_due",
    "payment_error",
    "used",
  ],
  red: [
    "offline",
    "disabled",
    "maintenance",
    "malfunction",
    "broken",
    "failed",
    "error",
    "canceled",
    "cancelled",
    "expired",
    "canceled_by_user_timeout",
    "canceled_by_low_battery",
    "suspended",
    "blocked",
    "lost",
    "stolen",
    "deleted",
  ],
  gray: ["inactive", "unverified"],
} as const;

export const getStatusDotColor = (
  status: string | undefined | null
): StatusColorClass => {
  if (!status) return "tw-bg-gray-400";
  const normalized = status.toLowerCase();

  if (STATUS_COLORS.green.includes(normalized as any)) return "tw-bg-green-500";
  if (STATUS_COLORS.blue.includes(normalized as any)) return "tw-bg-blue-500";
  if (STATUS_COLORS.yellow.includes(normalized as any))
    return "tw-bg-yellow-500";
  if (STATUS_COLORS.red.includes(normalized as any)) return "tw-bg-red-500";
  if (STATUS_COLORS.gray.includes(normalized as any)) return "tw-bg-gray-400";

  return "tw-bg-gray-400";
};

export const formatStatusText = (status: string | undefined | null): string => {
  if (!status) return "N/A";
  return status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const getBatteryDotColor = (
  percentage: number | undefined | null
): StatusColorClass => {
  if (percentage == null) return "tw-bg-gray-400";
  if (percentage > 50) return "tw-bg-green-500";
  if (percentage > 20) return "tw-bg-yellow-500";
  return "tw-bg-red-500";
};

export const getLockStatusDotColor = (
  isOpen: boolean | undefined | null
): StatusColorClass => {
  if (isOpen == null) return "tw-bg-gray-400";
  return isOpen ? "tw-bg-yellow-500" : "tw-bg-green-500";
};

const TRANSACTION_TYPE_COLORS = {
  blue: ["charge", "refund"],
  yellow: ["adjustment"],
  green: ["credit"],
} as const;

export const getTransactionTypeDotColor = (
  type: string | undefined | null
): StatusColorClass => {
  if (!type) return "tw-bg-gray-400";
  const normalized = type.toLowerCase();

  if (TRANSACTION_TYPE_COLORS.blue.includes(normalized as any))
    return "tw-bg-blue-500";
  if (TRANSACTION_TYPE_COLORS.yellow.includes(normalized as any))
    return "tw-bg-yellow-500";
  if (TRANSACTION_TYPE_COLORS.green.includes(normalized as any))
    return "tw-bg-green-500";

  return "tw-bg-gray-400";
};

export const getBooleanDotColor = (
  value: boolean | undefined | null
): StatusColorClass => {
  if (value == null) return "tw-bg-gray-400";
  return value ? "tw-bg-green-500" : "tw-bg-gray-400";
};

export const getStatusTextColor = (
  status: string | undefined | null
): string => {
  if (!status) return "tw-text-gray-600";

  const dotColor = getStatusDotColor(status);
  switch (dotColor) {
    case "tw-bg-green-500":
      return "tw-text-green-600";
    case "tw-bg-blue-500":
      return "tw-text-blue-600";
    case "tw-bg-yellow-500":
      return "tw-text-yellow-600";
    case "tw-bg-red-500":
      return "tw-text-red-600";
    default:
      return "tw-text-gray-600";
  }
};

export const getBatteryTextColor = (
  percentage: number | undefined | null
): string => {
  if (percentage == null) return "tw-text-gray-600";
  if (percentage > 50) return "tw-text-green-600";
  if (percentage > 20) return "tw-text-yellow-600";
  return "tw-text-red-600";
};

export const getLockStatusTextColor = (
  isOpen: boolean | undefined | null
): string => {
  if (isOpen == null) return "tw-text-gray-600";
  return isOpen ? "tw-text-yellow-600" : "tw-text-green-600";
};

export const getTransactionTypeTextColor = (
  type: string | undefined | null
): string => {
  if (!type) return "tw-text-gray-600";
  const normalized = type.toLowerCase();

  if (TRANSACTION_TYPE_COLORS.blue.includes(normalized as any))
    return "tw-text-blue-600";
  if (TRANSACTION_TYPE_COLORS.yellow.includes(normalized as any))
    return "tw-text-yellow-600";
  if (TRANSACTION_TYPE_COLORS.green.includes(normalized as any))
    return "tw-text-green-600";

  return "tw-text-gray-600";
};

export const getBooleanTextColor = (
  value: boolean | undefined | null
): string => {
  if (value == null) return "tw-text-gray-600";
  return value ? "tw-text-green-600" : "tw-text-gray-600";
};

const DOCK_TYPE_COLORS = {
  blue: ["charging", "charge"],
  green: ["parking", "park"],
  yellow: ["service"],
} as const;

export const getDockTypeDotColor = (
  type: string | undefined | null
): StatusColorClass => {
  if (!type) return "tw-bg-gray-400";
  const normalized = type.toLowerCase();

  if (DOCK_TYPE_COLORS.blue.includes(normalized as any))
    return "tw-bg-blue-500";
  if (DOCK_TYPE_COLORS.green.includes(normalized as any))
    return "tw-bg-green-500";
  if (DOCK_TYPE_COLORS.yellow.includes(normalized as any))
    return "tw-bg-yellow-500";

  return "tw-bg-gray-400";
};

export const getDockTypeTextColor = (
  type: string | undefined | null
): string => {
  if (!type) return "tw-text-gray-600";
  const normalized = type.toLowerCase();

  if (DOCK_TYPE_COLORS.blue.includes(normalized as any))
    return "tw-text-blue-600";
  if (DOCK_TYPE_COLORS.green.includes(normalized as any))
    return "tw-text-green-600";
  if (DOCK_TYPE_COLORS.yellow.includes(normalized as any))
    return "tw-text-yellow-600";

  return "tw-text-gray-600";
};

const TYPE_COLORS = {
  blue: ["WALLET"],
  green: ["PAYMENT"],
  emerald: ["TOP_UP"],
  orange: ["REFUND"],
} as const;

export const getTypeDotColor = (type: string): string => {
  const normalized = type?.toUpperCase();
  if (!normalized) return "tw-bg-gray-400";

  if (TYPE_COLORS.blue.includes(normalized as any)) return "tw-bg-blue-500";
  if (TYPE_COLORS.green.includes(normalized as any)) return "tw-bg-green-500";
  if (TYPE_COLORS.emerald.includes(normalized as any))
    return "tw-bg-emerald-500";
  if (TYPE_COLORS.orange.includes(normalized as any)) return "tw-bg-orange-500";

  return "tw-bg-gray-400";
};

export const getTypeTextColor = (type: string): string => {
  const normalized = type?.toUpperCase();
  if (!normalized) return "tw-text-muted-foreground";

  if (TYPE_COLORS.blue.includes(normalized as any)) return "tw-text-blue-600";
  if (TYPE_COLORS.green.includes(normalized as any)) return "tw-text-green-600";
  if (TYPE_COLORS.emerald.includes(normalized as any))
    return "tw-text-emerald-600";
  if (TYPE_COLORS.orange.includes(normalized as any))
    return "tw-text-orange-600";

  return "tw-text-muted-foreground";
};

const PARTICULARS_COLORS = {
  purple: ["admin"],
  emerald: ["top up"],
  red: ["trip"],
} as const;

export const getParticularsDotColor = (tag: string): string => {
  const normalized = tag?.toLowerCase();
  if (!normalized) return "tw-bg-gray-400";

  if (PARTICULARS_COLORS.purple.includes(normalized as any))
    return "tw-bg-purple-500";
  if (PARTICULARS_COLORS.emerald.includes(normalized as any))
    return "tw-bg-emerald-500";
  if (PARTICULARS_COLORS.red.includes(normalized as any))
    return "tw-bg-red-500";

  return "tw-bg-gray-400";
};

export const getParticularsTextColor = (tag: string): string => {
  const normalized = tag?.toLowerCase();
  if (!normalized) return "tw-text-muted-foreground";

  if (PARTICULARS_COLORS.purple.includes(normalized as any))
    return "tw-text-purple-600";
  if (PARTICULARS_COLORS.emerald.includes(normalized as any))
    return "tw-text-emerald-600";
  if (PARTICULARS_COLORS.red.includes(normalized as any))
    return "tw-text-red-600";

  return "tw-text-muted-foreground";
};
